import type { ShallowRef } from "vue"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { ReactiveConversation } from "~/classes/conversation/ReactiveConversation"
import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { ReactiveFile } from "~/classes/file/ReactiveFile"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendConversationPropertiesRequests } from "~/classes/handlers/conversation/properties"
import { sendFilePropertiesRequests } from "~/classes/handlers/file/properties"
import { sendSharedFolderFindRequests } from "~/classes/handlers/sharedFolder/find"
import { ReactiveSearch } from "~/classes/search/ReactiveSearch"
import { StaticSearch } from "~/classes/search/StaticSearch"

// These interfaces need to be serializable to JSON in order to pack up in the
// window history state
interface NavigationCollectionFolderState {
  navType: "collection-folder"
  rootId: string
  openFolders?: Record<string, boolean>
  sharedFolderHomeId?: string
}
interface NavigationCollectionSearchState {
  navType: "collection-search"
  searchId: string
  // Whether this is a newly created search or an existing search
  fresh: boolean
  // TODO: In a search, could you also have tree/column view!
  openFolders: Record<string, boolean>
}
interface NavigationFilePreviewState {
  navType: "preview-file"
  fileId: string // the path in our nav is this file's path
  sharedFolderHomeId?: string
}

interface NavigationConversationState {
  navType: "conversation"
  conversationId: string
  // TODO: scroll position saved here
}

interface NavigationSettingsState {
  navType: "settings"
  // TODO: scroll position saved here
}
type NavigationPageState = NavigationCollectionFolderState | NavigationCollectionSearchState | NavigationFilePreviewState | NavigationConversationState | NavigationSettingsState
type NavigationType = NavigationPageState["navType"]

class Navigation extends GlobalComposable<{
  appLoadingPromise?: Promise<void>
  prior?: NavigationPageState
  current?: NavigationPageState
  next?: NavigationPageState
}> {
  override onCreate = () => {
    if (window)
      window.addEventListener("popstate", this.onAfterPopState)
  }

  override onDestroy = () => {
    if (window)
      window.removeEventListener("popstate", this.onAfterPopState)
  }

  // These are shallow refs since we don't want to deeply unwrap the inner
  // objects.
  currentFilePreview: ShallowRef<ReactiveFile | undefined> = shallowRef()
  currentSearch: ShallowRef<ReactiveSearch | undefined> = shallowRef()
  currentRootFolder: ShallowRef<ReactiveFile | undefined> = shallowRef()
  currentSharedFolderHome: ShallowRef<ReactiveFile | undefined> = shallowRef()
  currentConversation: ShallowRef<ReactiveConversation | undefined> = shallowRef()

  currentSearchIsFresh = computed(() => {
    if (this.state.value.current?.navType !== "collection-search")
      return false
    return this.state.value.current.fresh
  })

  currentSearchResults = computed(() => {
    if (this.state.value.current?.navType !== "collection-search")
      return {}
    const search = this.currentSearch.value
    if (!search || search.isBlank())
      return {}
    return search.searchResults()
  })

  currentOpenFolders = computed(() => {
    if (this.state.value.current?.navType === "collection-folder")
      return this.state.value.current.openFolders ?? {}
    else if (this.state.value.current?.navType === "collection-search")
      return this.state.value.current.openFolders ?? {}
    return {}
  })

  initializeWorkers = async () => {
    const { waitForCacheWorkerToBeSetUp } = useCache()
    const { initSandboxWorker } = useSandbox()
    const { initTauriHandlers } = useTauri()
    await Promise.all([waitForCacheWorkerToBeSetUp(), initSandboxWorker(), initTauriHandlers()])
  }

  /**
   * Mounts the app once. If we have multiple callers that call this, then they will
   * receive a callback function that resolves when the function has
   * completed. If two callbacks call this at once with different auth levels,
   * that is a runtime error.
   * NOTE: should this go somewhere else?
   */
  waitUntilAppLoaded = async () => {
    if (!this.state.value.appLoadingPromise)
      this.state.value.appLoadingPromise = this._waitUntilAppLoaded()
    return await this.state.value.appLoadingPromise
  }

  _waitUntilAppLoaded = async () => {
    const { cacheState, sendCacheGuestLogin, sendCacheLogin } = useCache()
    const { state: myself, requestAllSelfDetails } = useSelf()

    if (cacheState.value.status === "uninitialized")
      await this.initializeWorkers()

    if (cacheState.value.status === "uninitialized") // still uninitialized
      throw new Error("Cache was not able to start up")

    if (cacheState.value.anonId && cacheState.value.status === "offlineAnon") {
      // so that we receive signal if we turn out to still be offline
      cacheState.value.status = "connectingAnon"
      const { promise, resolve } = Promise.withResolvers<any>()
      watchOnce(() => cacheState.value.status, resolve)
      sendCacheGuestLogin(cacheState.value.anonId)
      await promise
      // @ts-expect-error TODO: this is a bug in the types
      if (cacheState.value.status === "pendingAnon")
        return this.warn("Network offline, will start in offline mode")
    }

    if (cacheState.value.userId && cacheState.value.status === "offlineAuth") {
      // so that we receive signal if we turn out to still be offline
      cacheState.value.status = "connectingAuth"
      const { promise, resolve } = Promise.withResolvers<any>()
      watchOnce(() => cacheState.value.status, resolve)
      sendCacheLogin(cacheState.value.userId)
      await promise
      // @ts-expect-error TODO: this is a bug in the types
      if (cacheState.value.status === "pendingAuth") {
        this.warn("Network offline, will start in offline mode")
      }
    }

    if (["readyAuth", "pendingAuth"].includes(cacheState.value.status) && !myself.value.home) {
      const { promise, resolve } = Promise.withResolvers<any>()
      watchOnce(() => myself.value.home, resolve)
      requestAllSelfDetails()
      await promise
      if (!myself.value.home)
        throw new Error("Home is still not set somehow")
    }
  }

  isAppLoaded = () => {
    const { cacheState } = useCache()
    const { state: myself } = useSelf()
    return !!cacheState.value.anonId || !!myself.value.user
  }

  isOnNavType = (navType: NavigationType) => {
    return this.state.value.current?.navType === navType
  }

  isOnSearchPage = () => {
    return this.isOnNavType("collection-search")
  }

  isOnPreviewPage = () => {
    return this.isOnNavType("preview-file")
  }

  resetFileSystemNavigationState = () => {
    this.state.value = {}
  }

  /**
   * FIXME: The shared folder home could go stale if:
   * - We start offline and navigate to a folder in a shared folder.
   * - We go back online and find that the folder has been moved into a
   * different shared folder
   * - The `currentSharedFolderHome` still contains the old shared folder.
   *
   * This could be solved by registering a callback from the currentRootFolder
   * that calls a method every time the file is updated.
   */
  setCurrentRootFolder = (root: StaticFile, immediate: boolean) => {
    if (this.currentRootFolder.value?.id === root.id)
      this.warn("Setting current root folder to the same folder", root)
    const sharedFolderHome = this.getSharedFolderHomeFromStateOrPath(root)

    this.currentFilePreview.value = undefined
    this.currentConversation.value = undefined
    this.currentSearch.value = undefined
    this.currentRootFolder.value = new ReactiveFile({
      file: shallowRef(root),
      location: "nav",
      selection: InterfaceSelection.minimumPathSelection(),
      options: { noLifecycle: true, immediate },
    })
    if (sharedFolderHome)
      // immediately retrieve the shared folder home selection as it contains
      // a listing of all the users of the shared folder.
      this.currentSharedFolderHome.value = new ReactiveFile({
        file: shallowRef(sharedFolderHome),
        location: "nav",
        selection: InterfaceSelection.minimumSharedFolderHomeSelection(),
        options: { noLifecycle: true, immediate: true },
      })
    else
      this.currentSharedFolderHome.value = undefined
  }

  setCurrentFilePreview = (root: StaticFile, immediate: boolean) => {
    if (this.currentFilePreview.value?.id === root.id)
      this.warn("Setting current file preview to the same file", root)

    this.currentConversation.value = undefined
    this.currentSearch.value = undefined
    this.currentRootFolder.value = undefined
    this.currentSharedFolderHome.value = undefined
    this.currentFilePreview.value = new ReactiveFile({
      file: shallowRef(root),
      location: "nav",
      selection: InterfaceSelection.minimumPropertiesViewSelection(),
      options: { noLifecycle: true, immediate },
    })
  }

  setCurrentConversation = (conversation: StaticConversation) => {
    this.currentFilePreview.value = undefined
    this.currentConversation.value = undefined
    this.currentSearch.value = undefined
    this.currentRootFolder.value = undefined
    this.currentSharedFolderHome.value = undefined
    this.currentConversation.value = new ReactiveConversation({
      conversation: shallowRef(conversation),
      selection: InterfaceSelection.minimumConversationViewSelection(),
      location: "agent",
      options: { noLifecycle: true, immediate: true },
    })
  }

  setCurrentSearch = (search: StaticSearch) => {
    if (this.currentSearch.value?.id === search.id)
      this.warn("Setting current search to the same search", search)

    this.currentFilePreview.value = undefined
    this.currentConversation.value = undefined
    this.currentRootFolder.value = undefined
    this.currentSharedFolderHome.value = undefined
    this.currentSearch.value = new ReactiveSearch({
      search: shallowRef(search),
      selection: InterfaceSelection.minimumCachedSearchViewSelection(),
      location: "nav",
      options: { noLifecycle: true, immediate: true },
    })
  }

  setCurrentNavigationState = (current?: NavigationPageState) => {
    this.state.value.current = current
    this.setHistoryStateFromNavigationState()
  }

  setNextNavigationState = (next?: NavigationPageState) => {
    this.state.value.next = next
  }

  clearNextNavigationState = () => {
    this.state.value.next = undefined
  }

  setPriorNavigationState = (prior?: NavigationPageState) => {
    this.state.value.prior = prior
  }

  setNavigationStateToRootFolder = (root: StaticFile) => {
    const sharedFolderHome = this.getSharedFolderHomeFromStateOrPath(root)

    this.state.value.current = {
      navType: "collection-folder",
      rootId: root.id,
      openFolders: { [root.id]: true },
      sharedFolderHomeId: sharedFolderHome?.id,
    }
    this.setHistoryStateFromNavigationState()
  }

  setNavigationStateToFilePreview = ({
    file,
    sharedFolderHome,
  }: {
    file: StaticFile
    sharedFolderHome?: StaticFile
  }) => {
    const sharedFolderHomeId = sharedFolderHome?.id ?? file.rootSharedFolderId()
    this.state.value.current = {
      navType: "preview-file",
      fileId: file.id,
      sharedFolderHomeId,
    }
    this.setHistoryStateFromNavigationState()
  }

  setNavigationStateToSearch = ({
    search,
    fresh,
  }: {
    search: StaticSearch
    fresh: boolean
  }) => {
    if (this.state.value.current?.navType === "collection-search") {
      this.state.value.current.searchId = search.id
    }
    else {
      this.state.value.current = {
        navType: "collection-search",
        searchId: search.id,
        fresh,
        openFolders: {},
      }
    }
    this.setHistoryStateFromNavigationState()
  }

  /**
   * If the folder has a shared folder as its root, then we need to add
   * it to the navigation state. Use the shared folder from our shared
   * folder state if we have one, otherwise use the folder's own. If the file has
   * no valid shared folder, return undefined
   */

  getSharedFolderHomeFromStateOrPath = (file: StaticFile) => {
    const { state: sharedFolderState } = useSharedFolders()
    const sharedFolderId = file.rootSharedFolderId()
    if (isDefined(sharedFolderId)) {
      const home = sharedFolderState.value.all[sharedFolderId]?.home
      if (isDefined(home))
        return home
    }
    const root = file.rootFolder()
    if (root?.isSharedFolderHome())
      return root
    else if (root?.isSharedFolderArchive())
      this.warn("Fresh navigation to shared folder archive not fully implemented")
  }

  navigateToOnboarding = async () => {
    return navigateTo("/onboarding")
  }

  navigateToConversation = async (conversation: StaticConversation) => {
    const { addConversationIfNew: addOrUpdateConversation, setupCurrentConversationOrCreateNewOne } = useConversations()

    addOrUpdateConversation(conversation)
    conversation = await setupCurrentConversationOrCreateNewOne(conversation)

    this.setCurrentConversation(conversation)
    this.setNextNavigationState({
      navType: "conversation",
      conversationId: conversation.id,
    })
    return await navigateTo(`/conversation/id/${asShortId(conversation.id)}`)
  }

  /**
   * Creates a brand new conversation and navigates to it
   */
  navigateToNewConversation = async () => {
    const { createNewConversation, waitForConversationsToInitialize } = useConversations()
    await waitForConversationsToInitialize()
    const conversation = await createNewConversation()
    return this.navigateToConversation(conversation)
  }

  /**
   * For navigating to a "new" conversation, but will reuse an old one if you have already created a new
   * one, so that we have at most one new conversation going.
   */
  navigateToLatestOrNewConversation = async () => {
    const { getLatestIfEmptyOrCreateConversation, waitForConversationsToInitialize } = useConversations()
    await waitForConversationsToInitialize()
    const conversation = await getLatestIfEmptyOrCreateConversation()
    return this.navigateToConversation(conversation)
  }

  navigateToSelfHome = async () => {
    const { state: myself } = useSelf()
    if (!myself.value.home)
      throw new Error("Cannot navigate to home")

    this.setNextNavigationState({
      navType: "collection-folder",
      rootId: myself.value.home.id,
      openFolders: { [myself.value.home.id]: true },
    })
    return await this.navigateToFolder(myself.value.home)
  }

  navigateToSelfArchive = async () => {
    const { state: myself } = useSelf()
    if (!myself.value.archive)
      throw new Error("Cannot navigate to archive")

    this.setNextNavigationState({
      navType: "collection-folder",
      rootId: myself.value.archive.id,
      openFolders: { [myself.value.archive.id]: true },
    })
    return await this.navigateToFolder(myself.value.archive)
  }

  navigateToFolder = async (folder: StaticFile) => {
    const { state: myself } = useSelf()

    const folderId = folder.id

    // TODO: preemptively set the navigation state and 'currentRootFolder' so that we
    // see the new file immediately in the UI. We need to set this to immediate as we
    // are not certain that the folder provided has sufficient properties
    this.setCurrentRootFolder(folder, true)

    this.setNextNavigationState({
      navType: "collection-folder",
      rootId: folder.id,
      openFolders: { [folder.id]: true },
      sharedFolderHomeId: this.getSharedFolderHomeFromStateOrPath(folder)?.id,
    })

    if (myself.value.home && folderId === myself.value.home.id)
      return await navigateTo(`/home`)
    else if (myself.value.archive && folderId === myself.value.archive.id)
      return await navigateTo(`/archive`)
    else
      return await navigateTo(`/folder/id/${asShortId(folderId)}`)
  }

  navigateToFile = async (file: StaticFile) => {
    if (file.isBlank())
      return await navigateTo(`/file/id/${asShortId(file.id)}`)
    if (file.isFolder())
      return await this.navigateToFolder(file)
    if (file.isLink())
      return await this.navigateToLink(file)

    this.setCurrentFilePreview(file, true) // request needed props
    this.setNextNavigationState({
      navType: "preview-file",
      fileId: file.id,
      sharedFolderHomeId: this.getSharedFolderHomeFromStateOrPath(file)?.id,
    })

    return await navigateTo(`/file/id/${asShortId(file.id)}`)
  }

  navigateToLink = async (link: StaticFile) => {
    const { isAuthenticated } = useSelf()
    const url = link.linkDestinationUrl()
    if (!url)
      return this.error("File doesn't have a destination", link)

    if (isDesktop()) {
      const { launchWithDefaultApplication } = useTauri()
      return launchWithDefaultApplication(url)
    }

    if (isAuthenticated.value)
      link.setFileAccessedIfAllowed()

    window.open(url, "_blank")
  }

  setHistoryStateFromNavigationState = () => {
    if (!this.state.value.current)
      throw new Error("No current navigation state!")

    // Vue router has some stuff it saves in history so we need to retain that data, while
    // adding our own fields to the history.
    window.history.replaceState({
      ...window.history.state,
      navigationState: JSON.stringify(this.state.value.current),
    }, "")
  }

  openFolderInNewTab = async (folder: StaticFile) => {
    return await navigateTo(
      `/folder/id/${asShortId(folder.id)}`,
      { open: { target: "_blank" } },
    )
  }

  openFileInNewTab = async (file: StaticFile) => {
    if (file.isFolder())
      return await this.openFolderInNewTab(file)
    return await navigateTo(
      `/file/id/${asShortId(file.id)}`,
      { open: { target: "_blank" } },
    )
  }

  openSearchInNewTab = async (search: StaticSearch) => {
    return await navigateTo(
      `/search/id/${asShortId(search.id)}`,
      { open: { target: "_blank" } },
    )
  }

  /**
   * After a popstate event, we want to check if the state contains our own state object, and
   * if so, we load it ourselves as the "next", which will get loaded by the navigation
   * callback.
   */
  onAfterPopState = (event: PopStateEvent) => {
    this.debug("Popped history state", event)

    if (!event.state?.navigationState)
      return

    try {
      const historyState: NavigationPageState = JSON.parse(event.state.navigationState)
      if (!historyState.navType || !["collection-folder", "collection-search", "preview-file", "agent"].includes(historyState.navType))
        throw new Error("No navigation type to deserialize or wrong type")
      this.state.value.next = historyState
    }
    catch (error) {
      this.error("Could not deserialize json navigation state", error)
      return
    }
  }

  onAfterNavigatedToConversation = async (conversationId: string) => {
    const { switchMainView } = useMainView()
    const { clearSelected } = useSelection()
    const { emitInterfaceEvent, waitForFirstDataResponse } = useEvents()
    const { notifyError } = useAlerts()
    const { isOffline, isAuthenticated } = useCache()

    let convo = this.currentConversation.value?.asStatic()

    if (!isDefined(convo) || convo.id !== conversationId) {
      const requestId = generateWorkerRequestId()
      // TODO: These kinds of "wait for" events can become events within our reactive
      // file, so that we simply create the reactive file.
      const promise = waitForFirstDataResponse("conversationPropertiesResponse", requestId)
      sendConversationPropertiesRequests([{
        requestId,
        conversationId,
        selection: InterfaceSelection.minimumConversationViewSelection().inner(),
        offline: isOffline(),
      }])
      const response = await promise
      if (response.error || !response.conversation) {
        this.error("Received a navigation error", response)
        notifyError("Cannot access that conversation", response.error!.details)
        if (isAuthenticated())
          return await this.navigateToSelfHome()
        else {
          return await this.navigateToLoginScreenAndClearCredentials()
        }
      }
      convo = new StaticConversation(response.conversation)
    }

    this.setCurrentConversation(convo)
    this.setPriorNavigationState(this.state.value.current)
    this.setCurrentNavigationState({
      navType: "conversation",
      conversationId: convo.id,
    })

    switchMainView("agent")
    clearSelected() // TODO: needed?
    emitInterfaceEvent("afterNavigatedToConversation", convo)
  }

  onAfterNavigatedToFolder = async (fileId: string) => {
    const { emitInterfaceEvent, waitForFirstDataResponse } = useEvents()
    const { clearSelected } = useSelection()
    const { switchMainView } = useMainView()
    const { switchCollectionType } = useCollectionView()
    const { isOffline, isAuthenticated } = useCache()
    const { notifyError } = useAlerts()

    let root = this.currentRootFolder.value?.asStatic()

    if (!isDefined(root) || root.id !== fileId) {
      const requestId = generateWorkerRequestId()
      // TODO: These kinds of "wait for" events can become events within our reactive
      // file, so that we simply create the reactive file.
      const promise = waitForFirstDataResponse("filePropertiesResponse", requestId)
      sendFilePropertiesRequests([{ // request minimal properties
        requestId,
        fileId,
        selection: InterfaceSelection.minimumPathSelection().inner(),
        pathSelection: InterfaceSelection.minimumPathSelection().inner(),
        offline: isOffline(),
      }])
      const response = await promise
      if (response.error || !response.file) {
        this.error("Received a navigation error", response)
        notifyError("Cannot access that file", response.error!.details)
        if (isAuthenticated())
          return await this.navigateToSelfHome()
        else {
          return await this.navigateToLoginScreenAndClearCredentials()
        }
      }
      root = new StaticFile(response.file)
      this.setCurrentRootFolder(root, false)
      this.setNavigationStateToRootFolder(root)
    }
    // if we navigated here via a `navigateTo(folder)` call
    else if (this.state.value.next) {
      this.setCurrentNavigationState(this.state.value.next)
    // if we navigated here via a `navigateTo(/folder-id)` call
    }
    else {
      this.setNavigationStateToRootFolder(root)
    }

    this.setPriorNavigationState(this.state.value.current)
    this.clearNextNavigationState()

    switchMainView("collection")
    switchCollectionType("folder")
    clearSelected() // TODO: needed?
    emitInterfaceEvent("afterNavigatedToFolder", root)
  }

  onAfterNavigatedToFile = async (fileId: string) => {
    const { emitInterfaceEvent, waitForFirstDataResponse } = useEvents()
    const { clearSelected } = useSelection()
    const { switchMainView } = useMainView()
    const { isOffline, isAuthenticated } = useCache()
    const { notifyError } = useAlerts()

    let file = this.currentFilePreview.value?.asStatic()

    if (!isDefined(file) || file.id !== fileId) {
      const requestId = generateWorkerRequestId()
      // TODO: These kinds of "wait for" events can become events within our reactive
      // file, so that we simply create the reactive file.
      const promise = waitForFirstDataResponse("filePropertiesResponse", requestId)
      sendFilePropertiesRequests([{ // request minimal properties
        requestId,
        fileId,
        selection: InterfaceSelection.minimumPropertiesViewSelection().inner(),
        pathSelection: InterfaceSelection.minimumPathSelection().inner(),
        offline: isOffline(),
      }])
      const response = await promise
      if (response.error || !response.file) {
        this.error("Received a navigation error", response)
        notifyError("Cannot access that file", response.error!.details)
        if (isAuthenticated())
          return await this.navigateToSelfHome()
        else {
          return await this.navigateToLoginScreenAndClearCredentials()
        }
      }
      file = new StaticFile(response.file)
      this.setCurrentFilePreview(file, false)
      this.setCurrentNavigationState({
        navType: "preview-file",
        fileId: file.id,
        sharedFolderHomeId: this.getSharedFolderHomeFromStateOrPath(file)?.id,
      })
    }
    else {
      this.setCurrentNavigationState(this.state.value.next)
    }

    // if we set the current file preview via the navigateToFile, don't re-set it

    this.setPriorNavigationState(this.state.value.current)
    this.clearNextNavigationState()

    switchMainView("preview")
    clearSelected()
    emitInterfaceEvent("afterNavigatedToNonFolderFile", file)
  }

  navigateToSharedFolder = async (home: StaticFile) => {
    const handle = home.rootSharedFolderHandle()
    if (!isDefined(handle))
      return this.error("must have a handle at the moment")

    this.setCurrentRootFolder(home, true)
    this.setNextNavigationState({
      navType: "collection-folder",
      rootId: home.id,
      sharedFolderHomeId: home.id,
    })

    return await navigateTo(`/shared/${handle.handle}`)
  }

  onAfterNavigatedToSharedFolder = async (handle: string) => {
    const { emitInterfaceEvent, waitForFirstDataResponse } = useEvents()
    const { clearSelected } = useSelection()
    const { switchMainView } = useMainView()
    const { switchCollectionType } = useCollectionView()
    const { isOffline, isAuthenticated } = useCache()
    const { notifyError } = useAlerts()

    let root = this.currentRootFolder.value?.asStatic()

    if (!isDefined(root) || root.rootSharedFolderHandle()?.handle !== handle) {
      const requestId = generateWorkerRequestId()
      // TODO: These kinds of "wait for" events can become events within our reactive
      // file, so that we simply create the reactive file.
      const promise = waitForFirstDataResponse("sharedFolderFindResponse", requestId)
      sendSharedFolderFindRequests([{ // request minimal properties
        requestId,
        handle: { handle },
        offline: isOffline(),
      }])
      const response = await promise
      if (response.error || !response.home) {
        this.error("Received a navigation error", response)
        notifyError("Cannot access that shared folder", response.error!.details)
        if (isAuthenticated())
          return await this.navigateToSelfHome()
        else {
          return await this.navigateToLoginScreenAndClearCredentials()
        }
      }
      root = new StaticFile(response.home)
      this.setCurrentRootFolder(root, false)
      this.setNavigationStateToRootFolder(root)
    }
    else {
      this.setCurrentNavigationState(this.state.value.next)
    }

    this.setPriorNavigationState(this.state.value.current)
    this.clearNextNavigationState()

    switchMainView("collection")
    switchCollectionType("folder")
    clearSelected() // TODO: needed?
    emitInterfaceEvent("afterNavigatedToFolder", root)
  }

  navigateToLoginScreenAndClearCredentials = async () => {
    const { clearUserData } = useSelf()
    clearUserData()

    // clear Navigation state data to prevent offline navigation to stale data
    this.clearState()

    await navigateTo("/login")
  }

  navigateToSearch = async (search: StaticSearch, fresh: boolean) => {
    this.setCurrentSearch(search)
    this.setNextNavigationState({
      navType: "collection-search",
      searchId: search.id,
      fresh,
      openFolders: {},
    })
    return await navigateTo(`/search/id/${asShortId(search.id)}`)
  }

  onAfterNavigatedToSearch = (searchId: string) => {
    const { switchMainView } = useMainView()
    const { switchCollectionType } = useCollectionView()
    const { emitInterfaceEvent } = useEvents()
    const { clearSelected } = useSelection()

    // if we have no prior search here, i.e. we just landed on the page, since if we were
    // doing an internal navigation we would have set the search already (see
    // "navigateToSearch")

    let fresh = false // if we just navigated, it can't be fresh
    let nextSearch = this.currentSearch.value?.asStatic()

    if (!isDefined(nextSearch) || nextSearch.id !== searchId) {
      nextSearch = StaticSearch.blank(searchId)
      this.setCurrentNavigationState({
        navType: "collection-search",
        searchId,
        fresh,
        openFolders: {},
      })
    }
    else if (this.state.value.next?.navType === "collection-search") {
      this.setCurrentNavigationState(this.state.value.next)
      fresh = this.state.value.next.fresh
    }

    this.setCurrentSearch(nextSearch)

    this.setPriorNavigationState(this.state.value.current)
    this.clearNextNavigationState()

    switchMainView("collection")
    switchCollectionType("search-results")
    clearSelected() // TODO: needed?
    this.setNavigationStateToSearch({ search: nextSearch, fresh })
    emitInterfaceEvent("afterNavigatedToSearch", nextSearch)
  }

  onAfterNavigatedToSettings = () => {
    const { clearSelected } = useSelection()
    const { switchMainView } = useMainView()
    const { openSettings } = useSettings()

    // if we have no prior file here, i.e. we just landed on the page, since if we were
    // doing an internal navigation we would have set the path file already (see
    // "navigateToFolder")

    this.setPriorNavigationState(this.state.value.current)
    this.setCurrentNavigationState({ navType: "settings" })
    this.clearNextNavigationState()

    switchMainView("settings")
    openSettings()
    clearSelected() // TODO: needed?
  }

  /**
   * This handler basically logs out the user or redirects them to the home if there's an
   * invalid request when navigating to a search
   */
  onFreshSearchList = async (response: SearchListPageResponseDatagram) => {
    const { removeInterfaceEventListenerByRequestId } = useEvents()
    const { notifyError } = useAlerts()
    const { isAuthenticated } = useCache()

    // We only care about the first response, which will tell us if there was an auth
    // error.
    removeInterfaceEventListenerByRequestId("searchListPageResponse", response.requestId)

    if (response.error) {
      this.error("Received a navigation error", response)
      notifyError("Cannot access that search", response.error.details)
      if (isAuthenticated())
        return await this.navigateToSelfHome()
      else {
        return await this.navigateToLoginScreenAndClearCredentials()
      }
    }
  }
}

const navigation = new Navigation("navigation", theme.colors.violet.hex)
export default function () {
  return navigation.use()
}
